import axios from 'axios';
import { baseUrl } from '../app/api/apiSlice';

const MAX_SIZE_BYTES = 3 * 1024 * 1024 * 1024; // 3GB

/**
 * Request a presigned S3 upload URL from backend and upload file to S3.
 * @param {File} file - File object to upload
 * @param {string} token - Auth token
 * @param {string} [prefix] - Optional S3 prefix override (e.g. "av/webcam/")
 * @returns {Promise<{key: string, success: boolean, error?: string}>}
 */
export async function uploadFileToS3Presigned(file, token, prefix) {
  // Check file size
  if (file.size > MAX_SIZE_BYTES) {
    return { key: null, success: false, error: 'File too large. Maximum size is 3GB.' };
  }

  try {
    // 1. Request presigned URL
    const presignedRes = await axios.post(
      `${baseUrl}/api/presigned-upload-url/`,
      {
        filename: file.name,
        content_type: file.type,
        ...(prefix !== undefined && { prefix }),
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const { presigned_url, key } = presignedRes.data;

    // 2. Upload to S3
    const s3Res = await fetch(presigned_url, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        'x-amz-server-side-encryption': 'AES256',
      },
      body: file,
    });
    if (!s3Res.ok) {
      const errorBody = await s3Res.text();
      console.error('S3 upload failed:', s3Res.status, errorBody);
      return { key, success: false, error: `S3 upload failed: ${s3Res.status}` };
    }
    return { key, success: true };
  } catch (error) {
    return { key: null, success: false, error: error.message || 'Upload failed' };
  }
}

/**
 * Tell the backend an audio/webcam upload failed. The S3 PUT goes browser → S3
 * directly, so without this a denied write never shows up in our logs.
 * Best-effort: never throws.
 */
export function reportAvUploadFailure(sessionId, token, kind, detail) {
  return fetch(`${baseUrl}/api/v1/resources/chat_sessions/${sessionId}/av_upload_failed/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ kind, detail: String(detail) }),
  }).catch(() => {});
}
